import {NEW_ID} from '../../../../common/constants/index.js'
import ButtonPrimary from '../../../../common/components/Button/ButtonPrimary.js'
import ButtonSecondary from '../../../../common/components/Button/ButtonSecondary.js'
import {
  formSelector,
  getState,
  onlyIfAutoForm,
  useSelector,
} from '../../../../store.js'
import {
  fedEx360IntegrationIssueSelector,
  setShipper,
  shipperSelector,
} from '../../../../data/shippers.js'
import {isPresent} from '../../../../common/utils.js'
import {showConfirmMessageModal} from '../../../../common/components/Modal/ConfirmMessageModal.js'
import TextInput from '../../../../common/components/TextInput.js'
import {companySelector} from '../../../../data/company.js'
import verde from '../../../../common/verde.js'
import {isEmailAddress} from '../../../../common/email.js'
import Archive from './Archive.js'
import Nickname from './Nickname.js'
import ShippingMethods from './ShippingMethods.js'
import {FEDEX_360} from '../../../../common/constants/ShipperNames.js'
import {
  baseShipperFormErrorsSelector,
  baseShipperInitialFormSelector,
  goToShipperSettings,
  simpleSaveShipper,
} from '../ShippersFunctions.js'

export async function showFedEx360AuthModal() {
  const company = companySelector(getState())

  await showConfirmMessageModal({
    title: 'Initiate a New Account',
    Message: ({form, errors, updateModalForm}) => (
      <div>
        <TextInput
          id="email"
          label="Your Email"
          value={form.confirmValue}
          onChange={(confirmValue) => updateModalForm({confirmValue})}
          errorMessage={errors.email}
        />
      </div>
    ),
    confirmText: 'Get Started',
    cancelText: 'Cancel',
    confirmValue: company.email || '',
    errorsSelector(state, {formName}) {
      const errors = {}
      const form = formSelector(state, {formName})

      if (!isEmailAddress(form.confirmValue)) {
        errors.email = 'Email is required'

        errors.preventSave = true
      }

      return errors
    },
    async onResolve({outcome}) {
      if (!outcome) {
        return
      }

      const {json: shipper} = await verde.post('/fedex_360/initiate', {
        email: outcome,
      })

      if (shipper.id) {
        setShipper(shipper)
      }
    },
  })
}

export async function showFedEx360AuthUpdateModal({
  shipperID,
  needsSetupFinished,
}) {
  const shipper = shipperSelector(getState(), {shipperID})

  await showConfirmMessageModal({
    title: needsSetupFinished ? 'Finish Setup' : 'Edit Credentials',
    Message: ({form, errors, updateModalForm}) => (
      <div>
        {needsSetupFinished && (
          <p className="fs-01 lh-md margin-bottom-20">
            Enter the following info to complete setting up your FedEx account.
          </p>
        )}
        <div className="margin-bottom-20">
          <TextInput
            required
            id="carrier_account_id"
            label="Carrier Account ID"
            value={form.confirmValue.carrier_account_id}
            onChange={(carrier_account_id) =>
              updateModalForm({
                confirmValue: {
                  ...form.confirmValue,
                  carrier_account_id,
                  hasChanged_carrier_account_id: true,
                },
              })
            }
            errorMessage={
              form.hasChanged_carrier_account_id && errors.carrier_account_id
            }
          />
        </div>
        <div className="margin-bottom-20">
          <TextInput
            required
            id="developer_partner_id"
            label="Developer Partner ID"
            value={form.confirmValue.developer_partner_id}
            onChange={(developer_partner_id) =>
              updateModalForm({
                confirmValue: {
                  ...form.confirmValue,
                  developer_partner_id,
                  hasChanged_developer_partner_id: true,
                },
              })
            }
            errorMessage={
              form.hasChanged_developer_partner_id &&
              errors.developer_partner_id
            }
          />
        </div>
        <div className="margin-bottom-20">
          <TextInput
            required
            id="shipper_id"
            label="Shipper ID"
            value={form.confirmValue.shipper_id}
            onChange={(shipper_id) =>
              updateModalForm({
                confirmValue: {
                  ...form.confirmValue,
                  shipper_id,
                  hasChanged_shipper_id: true,
                },
              })
            }
            errorMessage={form.hasChanged_shipper_id && errors.shipper_id}
          />
        </div>
      </div>
    ),
    confirmText: 'Submit',
    cancelText: 'Cancel',
    confirmValue: needsSetupFinished
      ? {carrier_account_id: '', developer_partner_id: '', shipper_id: ''}
      : {
          carrier_account_id: shipper.vendor_config.carrier_account_id,
          developer_partner_id: shipper.vendor_config.developer_partner_id,
          shipper_id: shipper.vendor_config.shipper_id,
        },
    errorsSelector(state, {formName}) {
      const errors = {}
      const {
        confirmValue: {carrier_account_id, developer_partner_id, shipper_id},
      } = formSelector(state, {formName})

      if (!isPresent(carrier_account_id)) {
        errors.carrier_account_id = 'Carrier Account ID is required'
        errors.preventSave = true
      }

      if (!isPresent(developer_partner_id)) {
        errors.developer_partner_id = 'Developer Partner ID is required'
        errors.preventSave = true
      }

      if (!isPresent(shipper_id)) {
        errors.shipper_id = 'Shipper ID is required'
        errors.preventSave = true
      }

      return errors
    },
    async onResolve({outcome}) {
      if (!outcome) {
        return
      }

      const {json: shipper} = await verde.put(
        `/fedex_360/${shipperID}/finalize`,
        {
          shipper_id: outcome.shipper_id,
          carrier_account_id: outcome.carrier_account_id,
          developer_partner_id: outcome.developer_partner_id,
        },
      )

      if (shipper.id) {
        setShipper(shipper)
      }
    },
  })
}

export function setUpShipperForm({shipper, formName}) {
  return {
    formName,
    initialForm: {
      ...initialFormSelector(getState(), {shipper}),
      errorsSelector,
    },
  }
}

export function errorsSelector(state, {formName}) {
  return baseShipperFormErrorsSelector(state, {formName})
}

export function initialFormSelector(state, {shipper}) {
  return baseShipperInitialFormSelector(state, {
    shipper,
    vendor: FEDEX_360,
  })
}

export async function saveShipperFedEx360(formName) {
  return simpleSaveShipper(formName)
}

function FedEx360Form({formName, form}) {
  const {id, isSaving} = form
  const errors = useSelector((state) => form.errorsSelector(state, {formName}))
  const needsSetupFinished = !!useSelector((state) =>
    fedEx360IntegrationIssueSelector(state, {shipperID: id}),
  )

  return (
    <div className="settings-list-item-form-wrap clear-both">
      <div className="medium-7 columns">
        {id !== NEW_ID && (
          <fieldset>
            <ul className="form-list margin-bottom-0">
              {needsSetupFinished ? (
                <li className="form-list-item alert alert--warning margin-bottom-20">
                  <p className="fs-01 lh-md margin-bottom-5">
                    <strong>Additional action is required.</strong>
                  </p>
                  <p className="fs-00 lh-md margin-bottom-10">
                    To complete the setup process, add your Fedex Carrier
                    Account, Developer Partner and Shipper credentials.
                  </p>
                  <ButtonPrimary
                    size="sm"
                    onClick={() =>
                      showFedEx360AuthUpdateModal({
                        shipperID: id,
                        needsSetupFinished,
                      })
                    }
                  >
                    Add Credentials
                  </ButtonPrimary>
                </li>
              ) : (
                <li className="form-list-item alert alert--neutral margin-bottom-20">
                  <p className="fs-01 lh-md margin-bottom-7">
                    <strong>Your Fedex account is connected.</strong>
                  </p>
                  <ButtonPrimary
                    isOutlined
                    size="x-sm"
                    onClick={() =>
                      showFedEx360AuthUpdateModal({
                        shipperID: id,
                        needsSetupFinished,
                      })
                    }
                  >
                    Edit Credentials
                  </ButtonPrimary>
                </li>
              )}
              {<Nickname formName={formName} />}

              {<ShippingMethods formName={formName} />}
              <li className="list__item list__item--no-style">
                <ButtonPrimary
                  className="btn--primary-alt"
                  isLoading={isSaving}
                  isDisabled={errors.preventSave}
                  onClick={() => saveShipperFedEx360(formName)}
                >
                  Update
                </ButtonPrimary>
                <ButtonSecondary onClick={() => goToShipperSettings()}>
                  Cancel
                </ButtonSecondary>
              </li>
            </ul>
          </fieldset>
        )}
        {id === NEW_ID && (
          <fieldset>
            <ul className="form-list margin-bottom-0">
              <li className="form-list-item fs-00 margin-top-5">
                <ButtonPrimary onClick={() => showFedEx360AuthModal()}>
                  Add a FedEx Account
                </ButtonPrimary>
                <ButtonSecondary onClick={() => goToShipperSettings()}>
                  Cancel
                </ButtonSecondary>
              </li>
            </ul>
          </fieldset>
        )}
      </div>

      <div className="medium-5 columns">
        <div className="alert margin-bottom-10 alert--standard">
          {id === NEW_ID ? (
            <dl className="list">
              <dt className="list__title fs-00 lh-md margin-bottom-5">
                Need help authorizing your FedEx account?
              </dt>
              <dd className="list__item fs-n0 margin-bottom-0">
                <a
                  className="btn--link mid meta-ups-setup-help"
                  href="https://support.ordoro.com/fedex-setup/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Get step-by-step instructions →
                </a>
              </dd>
            </dl>
          ) : (
            <Archive formName={formName} />
          )}
        </div>
        <p className="fs-n1 lh-md op-75">
          FedEx service marks are owned by Federal Express Corporation and are
          used by permission.
        </p>
      </div>
    </div>
  )
}

export default onlyIfAutoForm(FedEx360Form, setUpShipperForm)
